import { useEffect, useLayoutEffect, useCallback } from 'react';

export interface UsePositionByPointOptions {
  popoverRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  /** Координаты точки. При alignY='top' — верхняя граница; при alignY='center' — центр по вертикали. */
  anchorPoint: { x: number; y: number } | null;
  /** Отступ от точки по горизонтали (вправо) */
  offsetX?: number;
  /** Отступ от точки по вертикали (вниз) при alignY='top' */
  offsetY?: number;
  /** Вертикальное выравнивание: 'top' — меню под/от точки; 'center' — центр меню по Y точки */
  alignY?: 'top' | 'center';
  /** Отступы от краёв viewport */
  margin?: number | Partial<{ top: number; bottom: number; left: number; right: number }>;
  zIndex?: number;
}

const DEFAULT_OFFSET = 8;
const DEFAULT_MARGIN = 20;

/**
 * Позиционирует поповер/меню относительно точки.
 * С учётом границ viewport при открытии. Без пересчёта при scroll документа; при resize и смене размера меню.
 */
export function usePositionByPoint({
  popoverRef,
  isOpen,
  anchorPoint,
  offsetX = DEFAULT_OFFSET,
  offsetY = DEFAULT_OFFSET,
  alignY = 'top',
  margin = DEFAULT_MARGIN,
  zIndex = 9999,
}: UsePositionByPointOptions) {
  const marginObj =
    typeof margin === 'number'
      ? { top: margin, bottom: margin, left: margin, right: margin }
      : { top: DEFAULT_MARGIN, bottom: DEFAULT_MARGIN, left: DEFAULT_MARGIN, right: DEFAULT_MARGIN, ...margin };

  const position = useCallback(() => {
    if (!anchorPoint || !popoverRef.current) return;
    const el = popoverRef.current;
    void el.offsetHeight;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = anchorPoint.x + offsetX;
    let top =
      alignY === 'center'
        ? anchorPoint.y - rect.height / 2
        : anchorPoint.y + offsetY;
    if (left + rect.width > vw - marginObj.right) left = vw - rect.width - marginObj.right;
    if (left < marginObj.left) left = marginObj.left;
    if (top + rect.height > vh - marginObj.bottom) top = vh - rect.height - marginObj.bottom;
    if (top < marginObj.top) top = marginObj.top;
    el.style.position = 'fixed';
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.zIndex = String(zIndex);
  }, [anchorPoint, offsetX, offsetY, alignY, marginObj.top, marginObj.bottom, marginObj.left, marginObj.right, zIndex, popoverRef]);

  useLayoutEffect(() => {
    if (!isOpen || !anchorPoint || !popoverRef.current) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(position);
      });
    });
  }, [isOpen, anchorPoint, position]);

  useEffect(() => {
    if (!isOpen || !anchorPoint || !popoverRef.current) return;
    let ticking = false;
    const handleInteraction = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          position();
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('resize', handleInteraction);
    const resizeObserver = new ResizeObserver(handleInteraction);
    if (popoverRef.current) resizeObserver.observe(popoverRef.current);
    return () => {
      window.removeEventListener('resize', handleInteraction);
      resizeObserver.disconnect();
    };
  }, [isOpen, anchorPoint, position, popoverRef]);
}
